/**
 * Organization selection page for users without active_tenant_id
 * Allows users to select their organization after login
 */
import { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/global/auth/AuthContext";
import { supabase } from "@/lib/supabase";
import { Button } from "@/global/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/global/components/ui/card";
import { LogOutButton } from "@/global/components/auth/LogOutButton";
import { Building2, ArrowRight } from "lucide-react";

interface Organization {
  id: string;
  name: string;
  type: "ORG" | "TEAM"; // Corrected to match schema
  fma_plan: string | null;
}

export function OrganizationSelectPage() {
  const {
    user,
    activeTenantId,
    selectTenant,
    loading: authLoading,
  } = useAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // If user already has active tenant, redirect to main app
  if (!authLoading && activeTenantId) {
    return <Navigate to="/" replace />;
  }

  // If not authenticated, redirect to login
  if (!authLoading && !user) {
    return <Navigate to="/login" replace />;
  }

  useEffect(() => {
    async function loadOrganizations() {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Get all organizations the user is a member of
        // Using RPC or manual join instead of PostgREST relationship
        const { data: memberships, error: membershipError } = await supabase
          .from("memberships")
          .select("organization_id")
          .eq("user_id", user.id)
          .eq("disabled", false);

        if (membershipError) throw membershipError;

        if (!memberships || memberships.length === 0) {
          setOrganizations([]);
          setLoading(false);
          return;
        }

        const orgIds = memberships.map((m) => m.organization_id);

        const { data: orgs, error: orgError } = await supabase
          .from("organizations")
          .select("id, name, type")
          .in("id", orgIds);

        if (orgError) throw orgError;

        const orgList =
          orgs?.map((org: any) => ({
            id: org.id,
            name: org.name,
            type: org.type,
            fma_plan: null,
          })) || [];

        setOrganizations(orgList);
      } catch (error) {
        console.error("Failed to load organizations:", error);
        setError("Failed to load your organizations. Please try again.");
      } finally {
        setLoading(false);
      }
    }

    loadOrganizations();
  }, [user]);

  const handleSelectOrganization = async (orgId: string) => {
    setSelecting(true);
    setError(null);

    try {
      await selectTenant(orgId);
      // Navigate to dashboard after successful tenant selection
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Failed to select organization:", error);
      setError("Failed to select organization. Please try again.");
      setSelecting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading your organizations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-2xl space-y-6">
        {/* Header */}
        <div className="text-center relative">
          <div className="absolute right-0 top-0">
            <LogOutButton variant="ghost" size="sm" />
          </div>
          <Building2 className="h-12 w-12 mx-auto mb-4 text-primary" />
          <h1 className="text-2xl font-bold">Select Your Organization</h1>
          <p className="text-muted-foreground mt-2">
            Choose which organization you'd like to access
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <Card className="border-destructive">
            <CardContent className="p-4">
              <p className="text-destructive text-sm">{error}</p>
            </CardContent>
          </Card>
        )}

        {/* Organization List */}
        {organizations.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Building2 className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">
                No Organizations Found
              </h3>
              <p className="text-muted-foreground mb-4">
                You don't appear to be a member of any organizations yet.
              </p>
              <p className="text-sm text-muted-foreground">
                Contact your administrator or{" "}
                <a
                  href="mailto:support@example.com"
                  className="text-primary hover:underline"
                >
                  reach out to support
                </a>{" "}
                for assistance.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {organizations.map((org) => (
              <Card
                key={org.id}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => !selecting && handleSelectOrganization(org.id)}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="h-12 w-12 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Building2 className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{org.name}</h3>
                        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                          <span className="capitalize">{org.type}</span>
                          {org.fma_plan && (
                            <>
                              <span>•</span>
                              <span className="capitalize">
                                {org.fma_plan.replace("fma_", "")} plan
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button disabled={selecting} variant="ghost" size="sm">
                      {selecting ? (
                        "Selecting..."
                      ) : (
                        <ArrowRight className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-sm text-muted-foreground">
          <p>
            Need help?{" "}
            <a
              href="mailto:support@example.com"
              className="text-primary hover:underline"
            >
              Contact support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
